import { spawn } from 'node:child_process';
import fs from 'node:fs';
import { tmpdir } from 'node:os';
import path from 'node:path';
import type { PreviewEnvironmentProcessLifecycle } from '../process-lifecycle.js';
import type { ShellSpawnResult } from './types.js';
import { isSpawnEbadf, mergePreviewEnvironmentSpawnOptions, rmDirQuiet } from './utils.js';

/**
 * Child stdout+stderr opened on a temp file (numeric fds). Often succeeds when pipe pairs throw EBADF.
 */
export function trySpawnMergedLogFile(
  command: string,
  env: NodeJS.ProcessEnv,
  cwd: string,
  signal?: AbortSignal,
  lifecycle?: PreviewEnvironmentProcessLifecycle,
): ShellSpawnResult | null {
  const tmpRoot = fs.mkdtempSync(path.join(tmpdir(), 'ba-devsrv-log-'));
  const logPath = path.join(tmpRoot, 'combined.log');
  let logFd: number;
  try {
    logFd = fs.openSync(logPath, 'a');
  } catch {
    rmDirQuiet(tmpRoot);
    return null;
  }
  const stdio: import('node:child_process').StdioOptions = ['ignore', logFd, logFd];
  try {
    let proc: import('node:child_process').ChildProcess;
    if (process.platform === 'win32') {
      proc = spawn(
        process.env.ComSpec || 'cmd.exe',
        ['/d', '/s', '/c', command],
        mergePreviewEnvironmentSpawnOptions(
          { env, cwd, stdio, windowsHide: true, ...(signal ? { signal } : {}) },
          lifecycle,
        ),
      );
    } else {
      proc = spawn(
        '/bin/sh',
        ['-c', command],
        mergePreviewEnvironmentSpawnOptions({ env, cwd, stdio, ...(signal ? { signal } : {}) }, lifecycle),
      );
    }
    fs.closeSync(logFd);
    return {
      proc,
      pipedStdoutStderr: true,
      mergedLogPath: logPath,
      mergedLogCleanupDir: tmpRoot,
    };
  } catch (e) {
    try {
      fs.closeSync(logFd);
    } catch {
      /* ignore */
    }
    rmDirQuiet(tmpRoot);
    if (isSpawnEbadf(e)) return null;
    throw e;
  }
}
